var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useDebounce } from '../../utils/hooks';
import { getClampedValue } from '../../utils/functions';
import SliderView from './SliderView';
var Slider = function (_a) {
    var min = _a.min, max = _a.max, step = _a.step, defaultValue = _a.defaultValue, marks = _a.marks, value = _a.value, color = _a.color, disabled = _a.disabled, onChange = _a.onChange, props = __rest(_a, ["min", "max", "step", "defaultValue", "marks", "value", "color", "disabled", "onChange"]);
    // is the slider handler being dragged?
    var _b = useState(false), moving = _b[0], setMoving = _b[1];
    // 0 is valid for value prop, have to check agaisnt undefined
    var _c = useState(value !== undefined ? value : defaultValue), internalValue = _c[0], setInternalValue = _c[1];
    var trackRef = useRef(null);
    var onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    // keep updating internal value only when prop is provided
    useEffect(function () {
        if (value !== undefined)
            setInternalValue(value);
    }, [value]);
    // 5 milisecond debounce on mousemove (manually adjusted to keep movement smooth without blocking thread)
    // TODO: test if 5ms looks good on other browsers/devices
    var handleMove = useDebounce(function (e) {
        updateValue(e);
    }, 5);
    var updateValue = useCallback(function (e) {
        var cursorX = e.clientX;
        // always get new track bounding rect as it's position can change with css or user scrolling through the page
        var _a = trackRef.current.getBoundingClientRect(), left = _a.left, width = _a.width;
        // exact new value
        var newValue = min + (max - min) * ((cursorX - left) / width);
        // snap value to closest step
        var remainder = (newValue % step) - (min % step);
        var snappedValue = remainder < step - remainder ? newValue - remainder : newValue + step - remainder;
        snappedValue = Math.round(Math.max(Math.min(snappedValue, max), min));
        // if slider is mounted on a 0 width window, calculation will fail so fallback to min
        if (isNaN(snappedValue) || snappedValue === undefined)
            snappedValue = min;
        onChangeRef.current(e, snappedValue || min);
        if (value === undefined)
            setInternalValue(snappedValue);
    }, []);
    // called when user releases right click after dragging handle
    var removeUpdateListener = useCallback(function () {
        window.removeEventListener('mousemove', handleMove);
        setMoving(false);
    }, []);
    useEffect(function () {
        // mousemove and mouseup events added to window so cursor doesn't have to stay exactly over handle button
        if (moving) {
            window.addEventListener('mousemove', handleMove);
            window.addEventListener('mouseup', removeUpdateListener);
        }
        // always try to remove event listeners on dismount for safety
        return function () {
            window.removeEventListener('mousemove', handleMove);
            window.removeEventListener('mouseup', removeUpdateListener);
        };
    }, [moving]);
    // in memo because marks array stays the same unless props of input are changed
    var marksArray = useMemo(function () {
        if (!marks)
            return [];
        // TODO: map marks to given them position
        var initialArray = [];
        if (Array.isArray(marks))
            initialArray = marks;
        else {
            // else marks is true... have to make an array of marks for each step
            for (var i = min; i < max; i += step)
                initialArray.push({ value: i, label: '' });
            initialArray.push({ value: max, label: '' }); // separate in case max is not multiple of step
        }
        // get position of each mark on the track
        var marksArray = initialArray.map(function (mark) { return (__assign({ position: "".concat((100 * (mark.value - min)) / (max - min), "%") }, mark)); });
        return marksArray;
    }, [marks, min, max]);
    // label click doesn't need mouse tracking to update value
    var handleLabelClick = function (e, index) {
        if (!disabled) {
            var newValue = marksArray[index].value;
            onChangeRef.current(e, newValue);
            if (value === undefined)
                setInternalValue(newValue);
        }
    };
    var handleTrackClick = function (e) {
        if (!disabled)
            updateValue(e);
    };
    // difference between uncontrolled input (controlled internaly) vs controlled input
    var displayValue = value !== undefined ? value : internalValue;
    var fill = getClampedValue((100 * (displayValue - min)) / (max - min), 0, 100);
    // activeMarks constantly changes when handle is moved
    var activeMarks = marksArray.map(function (mark) { return (__assign({ active: mark.value <= displayValue }, mark)); });
    // withLabels is true when any of the marks has a label that will be rendered
    var withLabels = !!activeMarks.find(function (mark) { return mark.label; });
    return (React.createElement(SliderView, __assign({ color: color, moving: moving, marks: activeMarks, withLabels: withLabels, value: getClampedValue(displayValue, min, max), trackRef: trackRef, handleHandleMouseDown: function () { return setMoving(true); }, disabled: disabled, fill: "".concat(fill, "%"), handleLabelClick: handleLabelClick, handleTrackClick: handleTrackClick }, props)));
};
Slider.defaultProps = {
    min: 0,
    max: 100,
    step: 1,
    color: 'primary',
    defaultValue: 50,
    marks: false,
    onChange: function () { },
};
export { Slider as default, Slider };
